/**
 * Open (or create) an evidence card on the Fusion Build tab.
 * Palette Evidence In rows use this so file/json/paste items get a real form
 * instead of a binding-only Run-rail chip or window.prompt.
 */
import { evidenceById, type InputField } from './registry';
import { useIntakeStore, newIntakeId } from '../stores/useIntakeStore';

function defaultInputs(fields?: InputField[]): Record<string, unknown> {
  const v: Record<string, unknown> = {};
  (fields || []).forEach((f) => {
    if (f.kind === 'toggle') v[f.key] = !!f.default;
    else if (f.kind === 'number') v[f.key] = f.default;
  });
  return v;
}

export type OpenBuildCardDetail = {
  registryId: string;
  /** Optional prefill (e.g. sample wallet). Merged onto defaults. */
  inputs?: Record<string, unknown>;
};

/** Ensure Build is started and the given evidence card is on the board (focused last). */
export function ensureBuildEvidenceCard(detail: OpenBuildCardDetail): string {
  const { registryId, inputs: prefill } = detail;
  const ev = evidenceById(registryId);
  const store = useIntakeStore.getState();
  const inputs = { ...defaultInputs(ev?.inputs), ...(prefill || {}) };
  const cardId = newIntakeId();

  if (!store.started) {
    store.startFrom('palette', [{ id: cardId, registryId, inputs, status: 'idle' }]);
  } else {
    // Prefer focusing an existing idle card of the same type over stacking duplicates.
    const idle = store.cards.find((c) => c.registryId === registryId && c.status === 'idle');
    if (idle) {
      if (prefill) {
        for (const [k, v] of Object.entries(prefill)) store.setInput(idle.id, k, v);
      }
      return idle.id;
    }
    store.addCard({ id: cardId, registryId, inputs, status: 'idle' });
  }
  return cardId;
}

/** Switch Fusion to Build and open the card (listened by FusionPage). */
export function openBuildEvidenceCard(detail: OpenBuildCardDetail): string {
  const cardId = ensureBuildEvidenceCard(detail);
  window.dispatchEvent(new CustomEvent('fusion:open-build-card', { detail: { ...detail, cardId } }));
  return cardId;
}
